import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";
import { Button } from "@/components/ui/button";
import { fetchPostBySlug } from "@/lib/blog";
import { fetchListingById, type Listing } from "@/lib/listings";
import { listingPath } from "@/lib/slugify";
import { usePageMeta } from "@/hooks/usePageMeta";

const BlogPost = () => {
  const { slug = "" } = useParams();
  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: () => fetchPostBySlug(slug),
    enabled: !!slug,
  });

  const [related, setRelated] = useState<Listing[]>([]);
  useEffect(() => {
    const ids = post?.similar_listing_ids || [];
    if (ids.length === 0) { setRelated([]); return; }
    Promise.all(ids.map((id) => fetchListingById(id).catch(() => null)))
      .then((res) => setRelated(res.filter((r): r is Listing => !!r && r.is_active !== false)));
  }, [post]);

  const seoTitle = post?.seo_title || post?.title;
  const seoDesc = post?.meta_description || post?.excerpt || (post?.title ? `${post.title} — Blog TopClass Imóveis` : undefined);
  const ogImage = post?.og_image || post?.cover_image || undefined;
  const url = post ? (post.canonical_url || `https://topclassimoveis.com/blog/${post.slug}`) : undefined;

  usePageMeta({ description: seoDesc, keywords: post?.keywords || post?.title });

  useEffect(() => {
    if (!post || !url) return;
    const scripts: HTMLScriptElement[] = [];
    const add = (obj: any) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      scripts.push(s);
    };

    add({
      "@context": "https://schema.org",
      "@type": post.schema_type || "Article",
      headline: post.title,
      description: seoDesc,
      image: ogImage,
      author: { "@type": "Organization", name: post.author || "TopClass Imóveis" },
      publisher: { "@type": "Organization", name: "TopClass Imóveis" },
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at,
      mainEntityOfPage: url,
    });

    add({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://topclassimoveis.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://topclassimoveis.com/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    });

    if (post.faqs && post.faqs.length > 0) {
      add({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({
          "@type": "Question", name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }

    return () => scripts.forEach((s) => document.head.removeChild(s));
  }, [post, url, seoDesc, ogImage]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16 container mx-auto px-4">
          <p className="text-center text-muted-foreground">Carregando...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16 container mx-auto px-4 text-center">
          <h1 className="font-display text-3xl font-bold mb-4">Post não encontrado</h1>
          <Link to="/blog" title="Voltar para o blog da TopClass Imóveis" className="text-accent hover:underline">Voltar ao blog</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const cta = post.cta || {};

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {seoTitle && <title>{seoTitle}</title>}
        {url && <link rel="canonical" href={url} />}
        {seoTitle && <meta property="og:title" content={seoTitle} />}
        {seoDesc && <meta property="og:description" content={seoDesc} />}
        {url && <meta property="og:url" content={url} />}
        <meta property="og:type" content="article" />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta name="twitter:card" content={post.twitter_card || "summary_large_image"} />
        {seoTitle && <meta name="twitter:title" content={seoTitle} />}
        {seoDesc && <meta name="twitter:description" content={seoDesc} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        {post.geo_region && <meta name="geo.region" content={post.geo_region} />}
        {post.geo?.latitude && post.geo?.longitude && (
          <meta name="geo.position" content={`${post.geo.latitude};${post.geo.longitude}`} />
        )}
      </Helmet>
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container mx-auto px-4 max-w-3xl">
          <Link to="/blog" title="Blog TopClass Imóveis">
            <Button variant="outline" size="sm" className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-1" /> Blog
            </Button>
          </Link>

          {/* Breadcrumb visível */}
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
            <Link to="/" title="Página inicial da TopClass Imóveis" className="hover:underline">Home</Link> ›{" "}
            <Link to="/blog" title="Blog TopClass Imóveis" className="hover:underline">Blog</Link> ›{" "}
            <span className="text-foreground">{post.title}</span>
          </nav>

          <h1 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-3">{post.title}</h1>
          {post.subtitle && <p className="text-xl text-muted-foreground font-body mb-4">{post.subtitle}</p>}
          <div className="flex items-center gap-4 text-sm text-muted-foreground font-body mb-8">
            {post.author && <span className="inline-flex items-center gap-1"><User className="h-4 w-4" /> {post.author}</span>}
            {post.published_at && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(post.published_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
              </span>
            )}
          </div>

          {post.cover_image && (
            <img src={post.cover_image} alt={post.title}
              className="w-full aspect-[16/9] object-cover rounded-lg mb-8 shadow-lg" />
          )}

          <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:font-body prose-p:text-foreground/90 prose-a:text-accent">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* Diferenciais */}
          {post.differentials && post.differentials.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-2xl font-bold mb-4">Diferenciais</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {post.differentials.map((d, i) => (
                  <div key={i} className="border border-border rounded-lg p-4">
                    <div className="font-semibold text-foreground">{d.title}</div>
                    {d.description && <p className="text-sm text-muted-foreground mt-1">{d.description}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Tipologias */}
          {post.typologies && post.typologies.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-2xl font-bold mb-4">Tipologias</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded">
                  <thead className="bg-muted"><tr>
                    <th className="p-2 text-left">Tipo</th><th className="p-2 text-left">Área</th>
                    <th className="p-2 text-left">Quartos</th><th className="p-2 text-left">Preço</th>
                  </tr></thead>
                  <tbody>{post.typologies.map((t, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-2">{t.name}</td><td className="p-2">{t.area}</td>
                      <td className="p-2">{t.bedrooms}</td><td className="p-2">{t.price}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
          )}

          {/* Comparativos */}
          {post.comparatives && post.comparatives.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-2xl font-bold mb-4">Comparativos</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {post.comparatives.map((c, i) => (
                  <div key={i} className="border border-border rounded p-3">
                    <div className="text-xs text-muted-foreground">{c.label}</div>
                    <div className="font-bold text-foreground">{c.value}</div>
                    {c.note && <div className="text-xs text-muted-foreground mt-1">{c.note}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* FAQ */}
          {post.faqs && post.faqs.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-2xl font-bold mb-4">Perguntas frequentes</h2>
              <div className="space-y-3">
                {post.faqs.map((f, i) => (
                  <details key={i} className="border border-border rounded p-3">
                    <summary className="font-semibold cursor-pointer">{f.question}</summary>
                    <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{f.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          {(cta.primary_text || cta.secondary_text) && (
            <section className="mt-10 bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
              <div className="flex flex-wrap justify-center gap-3">
                {cta.primary_text && cta.primary_url && (
                  <a href={cta.primary_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:opacity-90">
                    {cta.primary_text}
                  </a>
                )}
                {cta.secondary_text && cta.secondary_url && (
                  <a href={cta.secondary_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary/10">
                    {cta.secondary_text}
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Fontes */}
          {post.sources && post.sources.length > 0 && (
            <section className="mt-10 text-sm">
              <h2 className="font-display text-lg font-bold mb-2">Fontes</h2>
              <ul className="list-disc pl-5 space-y-1">
                {post.sources.map((s, i) => (
                  <li key={i}><a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{s.label || s.url}</a></li>
                ))}
              </ul>
            </section>
          )}

          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground">#{t}</span>
              ))}
            </div>
          )}

          {related.length > 0 && (
            <section className="mt-16 border-t border-border pt-10">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-6">Imóveis relacionados</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((l) => (
                  <Link key={l.id} to={listingPath(l)}
                    className="group block bg-card rounded-lg overflow-hidden border border-border hover:shadow-lg transition">
                    {l.images?.[0] && (
                      <img src={l.images[0]} alt={l.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    )}
                    <div className="p-4">
                      <div className="text-xs text-muted-foreground mb-1">{l.neighborhood}</div>
                      <h3 className="font-display font-bold text-foreground line-clamp-2">{l.title}</h3>
                      {l.price != null && (
                        <div className="text-accent font-bold mt-2">
                          {l.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <WhatsAppFloatingButton />
    </div>
  );
};

export default BlogPost;
